import { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import invoiceService from "@/services/billing/invoice/invoiceService";
import { PriceInput } from "@/components/form";
import { toast } from "sonner";
import { X } from "lucide-react";

const SettlementEditorModal = ({ row, refresh, fetchPaymentHistory }) => {
    const settlement =
        Array.isArray(row.settlements) && row.settlements.length > 0
            ? row.settlements[0]
            : null;

    const [open, setOpen] = useState(false);

    if (!settlement) return <span>-</span>;

    const validationSchema = Yup.object().shape({
        amount: Yup.number()
            .required("Amount is required")
            .moreThan(0, "Amount must be greater than 0"),
        payment_note: Yup.string().trim().required("Payment Note is required"),
    });

    const initialValues = {
        amount: settlement.amount || "",
        payment_note: row.payment_note || "",
    };

    const handleSubmit = async (values, { setSubmitting, setErrors }) => {
        try {
            const { status } = await invoiceService.updateSettlment(settlement.id, values);

            if (status) {
                toast.success("Settlement updated successfully");
                fetchPaymentHistory()
                refresh();
                setOpen(false);
            }
        } catch (error) {
            if (error.message === "422 Unprocessable Entity. Validation failed.") {
                const backendErrors = error.errors || {};
                const formatted = {};

                // Map errors to Formik format
                Object.entries(backendErrors).forEach(([field, msgs]) => {
                    formatted[field] = msgs[0]; // take first message
                });

                // Push errors INTO form fields
                setErrors(formatted);

                // Create toast summary text
                const errorText = Object.entries(formatted)
                    .map(([field, msg]) => `${field}: ${msg}`)
                    .join("\n");

                toast.warning("Validation Error", {
                    description:
                        errorText ||
                        "Please check your settlement fields. Some values may be invalid.",
                });

                setSubmitting(false);
                return;
            }

            toast.error("Failed to update settlement");
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {/* Trigger */}
            <span
                className="cursor-pointer link"
                onClick={() => setOpen(true)}
            >
                {settlement.amount ? `$${settlement.amount}` : "-"}
            </span>

            {/* Modal */}
            {open && (
                <div className="modal-wrapper modal-animate-fade">
                    <div className="modal-body modal-animate-scale w-4/5 max-w-xl relative">

                        {/* Header */}
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-medium text-gray-900 my-2">Edit {row?.number ? `INVOICE-${row.number}` : ""} Settlement</h2>
                            <button onClick={() => setOpen(false)}>
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                        >
                            {({ isSubmitting, setFieldValue, values }) => (
                                <Form className="space-y-4">

                                    {/* Amount */}
                                    <div>
                                        <label className="block text-sm font-medium mb-1">
                                            Amount
                                        </label>
                                        <PriceInput
                                            name="amount"
                                            prefix="$"
                                        />
                                    </div>

                                    {/* Payment Note */}
                                    <div>
                                        <label className="block text-sm font-medium mb-1">
                                            Payment Note
                                        </label>
                                        <Field
                                            as="textarea"
                                            name="payment_note"
                                            className="textarea textarea-bordered w-full"
                                            placeholder="Enter payment details…"
                                        />
                                        <ErrorMessage
                                            name="payment_note"
                                            component="div"
                                            className="text-xs text-red-500 mt-1"
                                        />
                                    </div>

                                    {/* Footer */}
                                    <div className="flex justify-end gap-2 pt-2">
                                        <button
                                            type="button"
                                            onClick={() => setOpen(false)}
                                            className="btn btn-outline"
                                        >
                                            Cancel
                                        </button>

                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="btn btn-primary"
                                        >
                                            {isSubmitting ? "Updating..." : "Update Settlement"}
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            )}
        </>
    );
};

export { SettlementEditorModal };
